import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService, WindowService, CommunityService, StorageKeys, StorageService, environment } from "@mypxplat/xplat/core";
import { TranslateService } from "@ngx-translate/core";
import { AppService, AvoService } from "@mypxplat/xplat/web/core";
import { filter, take } from "rxjs/operators";

const anyOn = (s: any): boolean => !!(s?.fender_news || s?.fender_news_intl || s?.fender_studio_news || s?.presonus_news);

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-account-notifications",
  templateUrl: "./notifications.component.html",
  standalone: false,
})
export class NotificationsComponent extends BaseComponent implements OnInit {
  public wantsEmail: any;
  public wantsProductEmail: boolean;
  public optoutEmail: boolean;
  public emmaiFrame: any;
  public saveButtonText: string;
  public saving: boolean = false;
  public resubscribeImgSrc: string;
  public env = environment;
  public email_settings: any;
  public fenderEmailSettings: {
    fender_news: boolean;
    fender_news_intl: boolean;
    fender_studio_news: boolean;
    presonus_news: boolean;
  };
  private lastSavedFenderEmailSettings: NotificationsComponent["fenderEmailSettings"];
  public communityPreferences: any = {
    all: true,
    mentions: true,
    direct_messages: true,
    post_replies: true,
    reply_replies: true,
    none: false,
  };
  protected bluecoreAction: string;

  private CRM_INTEREST_IDS = ["acoustic_guitar", "bass_guitar", "guitar", "fender_custom_shop", "music_production", "squier"];
  public crmInterests: Array<{ string_id: string; title: string; iterable_mapping: string; id: number; selected: boolean }> = [];
  public interestsLoading = false;

  get fenderNewsEnabled(): boolean {
    return this.user?.country === "US" ? this.fenderEmailSettings?.fender_news : this.fenderEmailSettings?.fender_news_intl;
  }

  private allUserSkills: any[] = [];

  constructor(
    public userService: UserService,
    private _translate: TranslateService,
    private _win: WindowService,
    public communityService: CommunityService,
    public appService: AppService,
    private avoService: AvoService,
    private storage: StorageService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.email_settings = this.user.email_settings;
    this.fenderEmailSettings = this.user.fender_email_settings || {
      fender_news: false,
      fender_news_intl: false,
      fender_studio_news: false,
      presonus_news: false,
    };
    // A copy, not the reference: the switches mutate user.fender_email_settings in place.
    this.lastSavedFenderEmailSettings = { ...this.fenderEmailSettings };
    this.setupNotificationPrefs();
    this.saveButtonText = this._translate.instant("general.save");
    this.loadCrmInterests();
  }

  private loadCrmInterests() {
    this.interestsLoading = true;
    this.communityService.getAvailableSkills().subscribe({
      next: (skills) => {
        this.communityService.getUserSkills().subscribe({
          next: (mySkills: any) => {
            this.allUserSkills = mySkills || [];
            const mySkillIds = new Set(this.allUserSkills.map((s: any) => s.skill_id));
            this.crmInterests = skills
              .filter((s: any) => this.CRM_INTEREST_IDS.includes(s.string_id))
              .map((s: any) => ({
                string_id: s.string_id,
                title: s.title,
                iterable_mapping: s.iterable_mapping,
                id: s.id,
                selected: mySkillIds.has(s.id),
              }));
            this.interestsLoading = false;
          },
          error: () => {
            this.crmInterests = skills
              .filter((s: any) => this.CRM_INTEREST_IDS.includes(s.string_id))
              .map((s: any) => ({
                string_id: s.string_id,
                title: s.title,
                iterable_mapping: s.iterable_mapping,
                id: s.id,
                selected: false,
              }));
            this.interestsLoading = false;
          },
        });
      },
      error: () => {
        this.interestsLoading = false;
      },
    });
  }

  toggleFenderNews() {
    if (this.user?.country === "US") {
      this.fenderEmailSettings.fender_news = !this.fenderEmailSettings.fender_news;
    } else {
      this.fenderEmailSettings.fender_news_intl = !this.fenderEmailSettings.fender_news_intl;
    }
    this.saveIterablePreferences();
  }

  toggleInterest(interest: { string_id: string; iterable_mapping: string; id: number; selected: boolean }) {
    interest.selected = !interest.selected;
    this.trackInterestToggle(interest);

    // Build full skills list: non-CRM skills the user already has + currently selected CRM interests
    const crmSkillIds = new Set(this.crmInterests.map((i) => i.id));
    const nonCrmSkills = this.allUserSkills.filter((s: any) => !crmSkillIds.has(s.skill_id));
    const selectedCrmSkills = this.crmInterests.filter((i) => i.selected);

    const args = [
      ...nonCrmSkills.map((s: any) => ({
        user_id: this.user.id,
        skill_id: s.skill_id,
        hourly_rate: s.hourly_rate || null,
        notes: s.notes || null,
        is_service: s.is_service || null,
      })),
      ...selectedCrmSkills.map((i) => ({
        user_id: this.user.id,
        skill_id: i.id,
      })),
    ];
    const string_ids = [...nonCrmSkills.map((s: any) => s.string_id), ...selectedCrmSkills.map((i) => i.string_id)];

    this.storage.removeItem(StorageKeys.USER_SKILLS);
    this.communityService.addUserSkills(args).subscribe({
      next: () => {
        // Update local skills list so subsequent toggles use fresh data
        this.allUserSkills = [...nonCrmSkills, ...selectedCrmSkills.map((i) => ({ skill_id: i.id, string_id: i.string_id }))];
        // Sync to Iterable
        const interestsMap: Record<string, boolean> = {};
        this.crmInterests.forEach((i) => {
          if (i.iterable_mapping) interestsMap[i.iterable_mapping] = i.selected;
        });
        this.userService.saveIterableSettings({ interests: interestsMap }).subscribe({
          next: () => this.userService.getUserDetails(false).subscribe(),
        });
      },
      error: () => {
        interest.selected = !interest.selected;
      },
    });
  }

  private trackInterestToggle(interest: { string_id: string; selected: boolean }) {
    if (this.userService.appId !== "fender") return;
    this.avoService.avoInitted$
      .pipe(
        filter((v) => !!v),
        take(1)
      )
      .subscribe(() => {
        const te = this.avoService.trackEvent();
        if (te && typeof (te as any).ctaClicked === "function") {
          const page = this.avoService.getPageProperties({ pageName: "Communication Preferences" });
          const extra = {
            ctaText: interest.selected ? `Enabled ${interest.string_id}` : `Disabled ${interest.string_id}`,
            ctaLocation: "CRM Interests",
          };
          const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
          (te as any).ctaClicked(payload);
        }
      });
  }

  saveIterablePreferences() {
    this.saveButtonText = this._translate.instant("general.saving_dots");
    if (!this.user.active_subscription && this.email_settings) this.email_settings.removeS1PlusMessageType = true;
    if (this.bluecoreAction && this.email_settings) this.email_settings.bluecoreSubscriptionEvent = this.bluecoreAction;
    let args = this.email_settings;
    args = this.fenderEmailSettings;
    const sent = { ...this.fenderEmailSettings };
    if (!anyOn(this.lastSavedFenderEmailSettings) && anyOn(sent)) args = { ...sent, demoSource: "my_fender_preference_center" };
    this.userService.saveIterableSettings(args).subscribe((result: any) => {
      if (result.success) {
        this.lastSavedFenderEmailSettings = sent;
        this._win.setTimeout(() => {
          this.userService.getUserDetails(false).subscribe(() => {
            this.email_settings = this.user.email_settings;
            if (this.user.fender_email_settings) this.fenderEmailSettings = this.user.fender_email_settings;
            this.saveButtonText = this._translate.instant("general.saved");
            this._win.setTimeout(() => {
              this.saving = false;
              this.saveButtonText = this._translate.instant("general.save");
              this.bluecoreAction = null;
            }, 1000);
          });
        }, 6000);
      } else {
        this.saveButtonText = this._translate.instant("general.save");
        this.saving = false;
        alert(result.message || result.error);
      }
    });
  }

  setupNotificationPrefs() {
    this.wantsEmail = this.user.wantsEmail;
    this.wantsProductEmail = this.user.wantsProductEmail;
    this.optoutEmail = this.user.optoutEmail;
  }

  setOptOut(value) {
    this.optoutEmail = !this.optoutEmail;
    if (value) {
      this.resubscribeImgSrc = "http://e.presonus.com/public/?q=direct_add&fn=Public_DirectAddForm&id=aczejuwnafsxuggizrtlcfxejodybpd&email=" + this.user.email;
    } else {
      this.resubscribeImgSrc = undefined;
    }
  }

  saveNotificationPrefs() {
    let args = {
      wantsEmail: this.wantsEmail,
      wantsProductEmail: this.wantsProductEmail,
      optoutEmail: this.optoutEmail,
    };
    this.saveButtonText = this._translate.instant("general.saving_dots");
    this.userService.saveNotifications(args).subscribe((result: any) => {
      if (result.success) {
        this.userService.getUserDetails(false).subscribe(() => {
          if (result.optin_src) {
            window.open(result.optin_src);
          }
          this.saveButtonText = this._translate.instant("general.saved");
          this._win.setTimeout(() => {
            this.saving = false;
            this.saveButtonText = this._translate.instant("general.save");
          }, 1000);
        });
      } else {
        this.saveButtonText = this._translate.instant("general.save");
        this.saving = false;
        alert(result.message || result.error);
      }
    });
  }
}
